import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareRelation = (r1, r2) => r1.jobid.toString() === r2.jobid.toString() && r1.relatedid.toString() === r2.relatedid.toString();

const jobRelationSlice = createSlice({
    name: 'jobRelation',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            let data = action.payload;
            if (!Array.isArray(data)) {
                data = [data];
            }
            data.forEach(dt => {
                const jobid = dt.jobid?.toString();
                delete state.data[jobid];
            });
            data.forEach(dt => {
                const jobid = dt.jobid?.toString();
                if (!state.data[jobid]) {
                    state.data[jobid] = []
                }
                state.data[jobid].push(dt);
            });
        },
        addOrUpdate: (state, action) => {
            for (const relation of ensureArray(action.payload)) {
                const jobid = relation.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareRelation(a, relation));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1, relation);
                    } else {
                        state.data[jobid].push(relation);
                    }
                } else {
                    state.data[jobid] = [relation];
                }
            }
        },
        remove: (state, action) => {
            for (const relation of ensureArray(action.payload)) {
                const jobid = relation.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareRelation(a, relation));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobRelationSlice.getInitialState());
    }
});

// #region network operations
export const fetchJobRelations = jobid => network => network.get('/api/v1/relatedjobs/' + encodeURIComponent(jobid), jobRelationSlice.actions.set);
export const addJobRelation = (network, related) => network.post('/api/v1/relatedjob', related, jobRelationSlice.actions.addOrUpdate);
export const removeJobRelation = (network, related) => network.delete(`/api/v1/relatedjob/${encodeURIComponent(related.jobid)}/${encodeURIComponent(related.relatedid)}`, jobRelationSlice.actions.remove);
// #endregion

// #region selectors
const selectRelations = state => state.jobRelation.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectRelationsByJobID = createSelector([selectRelations, (relations, jobid) => jobid], (relations, jobid) => {
    return relations[jobid?.toString()] ?? []
});
const selectRelatedJobs = jobid => state => selectRelationsByJobID(state, jobid);
// #endregion

// #region listeners
const listen = {
    jobRelatedAdded: (assistance, { dispatch }) => dispatch(jobRelationSlice.actions.addOrUpdate(assistance)),
    jobRelatedRemoved: (assistance, { dispatch }) => dispatch(jobRelationSlice.actions.remove(assistance))
}
// #endregion

// make sure we don't keep making new objects etc to reduce re-rendering and effect cascades
export const [useRelatedJobs,realizeRelatedJobs] = dataHookFactory(selectRelatedJobs, initMapFactory(fetchJobRelations, listen, jobid => jobid?.toString()));

const reducerDetails = { name: jobRelationSlice.name, reducer: jobRelationSlice.reducer };
export default reducerDetails;